import * as React from "react";
import { Search, Sparkles, Upload } from "lucide-react";
import { HoloShimmer } from "@/components/Holo";
import { Badge, Button, Combobox, Input, InputGroup, Separator, Skeleton, Spinner, Tabs } from "@/components/ui";
import { CoachOption, CoachPicker } from "@/containers/Coach/PromptStudio/components/CoachPicker";
import { ScopeToggle } from "@/containers/Coach/PromptStudio/components/ScopeToggle";
import { useInfiniteSentinel } from "@/containers/Coach/PromptStudio/hooks/useInfiniteSentinel";
import { cn } from "@/lib/utils";
import {
  BACKGROUND_FACETS,
  BackgroundAsset,
  BackgroundFacet,
  BackgroundRatio,
  BackgroundRequest,
  BackgroundSearchBody,
  BackgroundSource,
  BackgroundTier,
} from "@/services/bedrock/dtos/BackgroundAsset";
import { ContextType, DesignBackground } from "@/services/bedrock/dtos/DesignPackage";
import { BackgroundTileCrop } from "./BackgroundTileCrop";
import { FACET_TITLES, facetLabel, SOURCE_LABELS, SOURCE_TITLE, TIER_LABELS, TIER_TITLE } from "./facetLabels";
import { aspectClass } from "./ratios";
import { useBackgroundLibrary } from "./useBackgroundLibrary";

export type PickerMode = "coach" | "player" | "admin";

export interface AdminPickerScope {
  scope: "global" | "coach";
  coach: CoachOption | null;
  onScopeChange(scope: "global" | "coach"): void;
  onCoachChange(coach: CoachOption | null): void;
}

export interface BackgroundPickerProps {
  mode: PickerMode;
  /** Every tile is cropped to this: the slide's first slot ratio in the tray, the design's ratio in the share flow. */
  ratio: BackgroundRatio;
  /** `suitableFor` filter; review hides people. */
  contextType?: ContextType;
  /** Pinned first as "Suggested for this design". */
  suggested?: DesignBackground[];
  selectedAssetId?: number | null;
  /** Already attached: shown with a check and disabled. */
  excludeAssetIds?: number[];
  /** Ranks by relevance when given. */
  wants?: BackgroundSearchBody["wants"];
  canUpload?: boolean;
  canGenerate?: boolean;
  /** False shows the suggested row (and action tiles) alone: no search, chips, or library grid. Default true. */
  searchable?: boolean;
  /** The upload tile's label; defaults to "Upload a photo". */
  uploadLabel?: string;
  onSelect(asset: BackgroundAsset | DesignBackground): void;
  onUpload?(file: File): void;
  onGenerate?(): void;
  uploading?: boolean;
  uploadProgress?: number | null;
  /** Shimmer tiles with the estimate. */
  pendingRequests?: BackgroundRequest[];
  /** Admin mode only: the page owns the Global | Coach scope and the picked coach, so its own selector and this one agree. */
  adminScope?: AdminPickerScope;
  className?: string;
}

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 24;
const SKELETON_TILES = 6;
const UPLOAD_ACCEPT = "image/jpeg,image/png";

const PICKER_FACETS: BackgroundFacet[] = ["setting", "mood", "season", "region", "occasion"];
const ADMIN_FACETS = Object.keys(BACKGROUND_FACETS) as BackgroundFacet[];

type ChipKey = BackgroundFacet | "source" | "tier";
type Chips = Partial<Record<ChipKey, string[]>>;
type Scope = "all" | "coach" | "global";

interface ChipOption {
  value: string;
  label: string;
}

function chipOptions(key: ChipKey): ChipOption[] {
  if (key === "source") {
    return (Object.keys(SOURCE_LABELS) as BackgroundSource[]).map(value => ({ value, label: SOURCE_LABELS[value] }));
  }
  if (key === "tier") {
    return (Object.keys(TIER_LABELS) as BackgroundTier[]).map(value => ({ value, label: TIER_LABELS[value] }));
  }
  return BACKGROUND_FACETS[key].map(value => ({ value, label: facetLabel(key, value) }));
}

function chipTitle(key: ChipKey): string {
  if (key === "source") return SOURCE_TITLE;
  if (key === "tier") return TIER_TITLE;
  return FACET_TITLES[key];
}

const FacetChip: React.FC<{ chip: ChipKey; values: string[]; onChange(values: string[]): void }> = ({
  chip,
  values,
  onChange,
}) => {
  const options = React.useMemo(() => chipOptions(chip), [chip]);
  return (
    <Combobox
      multiple
      items={options}
      value={options.filter(option => values.includes(option.value))}
      isItemEqualToValue={(option, selected) => option.value === selected?.value}
      itemToStringLabel={option => option.label}
      onValueChange={(selected: ChipOption[] | null) => onChange((selected ?? []).map(option => option.value))}
      placeholder={chipTitle(chip)}
      className="w-auto min-w-36 shrink-0 bg-white"
      maxChips={1}
    />
  );
};

export function creatingLabel(estimatedWaitSeconds: number | null): string {
  if (estimatedWaitSeconds == null || estimatedWaitSeconds <= 0) return "Creating…";
  return `Creating… about ${Math.max(1, Math.ceil(estimatedWaitSeconds / 60))} min`;
}

function suggestedTile(background: DesignBackground) {
  return {
    id: background.assetId,
    url: background.url,
    focal: background.focal,
    source: background.source,
    description: null,
    ratios: background.ratios,
    compatible: background.compatible,
  };
}

const ActionTile: React.FC<{
  ratio: BackgroundRatio;
  icon: React.ReactNode;
  label: string;
  onClick(): void;
  disabled?: boolean;
  children?: React.ReactNode;
}> = ({ ratio, icon, label, onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 p-2 text-center text-xs font-medium text-slate-600 cursor-pointer hover:border-primary hover:text-primary disabled:cursor-default disabled:opacity-60",
      aspectClass(ratio)
    )}
  >
    {children ?? icon}
    <span>{label}</span>
  </button>
);

/**
 * Search over the coach's and the shared background library, cropped to the slot the picture will
 * fill. Presentation plus its own query state: selecting calls `onSelect` and nothing is changed here.
 */
export const BackgroundPicker: React.FC<BackgroundPickerProps> = ({
  mode,
  ratio,
  contextType,
  suggested = [],
  selectedAssetId,
  excludeAssetIds = [],
  wants,
  canUpload,
  canGenerate,
  searchable = true,
  uploadLabel = "Upload a photo",
  onSelect,
  onUpload,
  onGenerate,
  uploading,
  uploadProgress,
  pendingRequests = [],
  adminScope,
  className,
}) => {
  const [searchText, setSearchText] = React.useState("");
  const [q, setQ] = React.useState("");
  const [chips, setChips] = React.useState<Chips>({});
  const [ownScope, setOwnScope] = React.useState<Scope>(mode === "admin" ? "global" : "all");
  const [ownCoach, setOwnCoach] = React.useState<CoachOption | null>(null);
  const controlled = mode === "admin" && adminScope !== undefined;
  const scope: Scope = controlled ? adminScope.scope : ownScope;
  const coach = controlled ? adminScope.coach : ownCoach;
  const setScope = (next: Scope) =>
    controlled ? adminScope.onScopeChange(next as AdminPickerScope["scope"]) : setOwnScope(next);
  const setCoach = (next: CoachOption | null) => (controlled ? adminScope.onCoachChange(next) : setOwnCoach(next));
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const timer = setTimeout(() => setQ(searchText.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const chipKeys: ChipKey[] = mode === "admin" ? [...ADMIN_FACETS, "source", "tier"] : PICKER_FACETS;
  const filtering = q.length > 0 || Object.values(chips).some(values => (values?.length ?? 0) > 0);
  const hasInput = filtering || searchText.trim().length > 0;

  const body: BackgroundSearchBody = { ratio, perPage: PAGE_SIZE };
  if (mode !== "player") body.scope = scope;
  if (mode === "admin" && scope === "coach" && coach) body.coachId = coach.id;
  if (q) body.q = q;
  if (contextType) body.suitableFor = contextType;
  if (wants) {
    body.wants = wants;
    body.sort = "relevance";
  }
  chipKeys.forEach(key => {
    const values = chips[key];
    if (values && values.length > 0) (body as Record<string, unknown>)[key] = values;
  });

  const waitingForCoach = mode === "admin" && scope === "coach" && !coach;
  const { assets, fetchNextPage, hasNextPage, isLoading, isFetching, isError, refetch } = useBackgroundLibrary(body, {
    enabled: searchable && !waitingForCoach,
  });

  const sentinelRef = useInfiniteSentinel({
    enabled: !!hasNextPage && !isFetching,
    onIntersect: () => void fetchNextPage(),
  });

  const clear = () => {
    setSearchText("");
    setQ("");
    setChips({});
  };

  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onUpload?.(file);
  };

  const showSuggested = suggested.length > 0 && !filtering;
  const excluded = new Set(excludeAssetIds);

  const actionTiles = (
    <>
      {canUpload && (
        <ActionTile
          ratio={ratio}
          icon={<Upload className="size-5" aria-hidden="true" />}
          label={uploadLabel}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <span className="flex items-center gap-1" role="progressbar" aria-valuenow={uploadProgress ?? undefined}>
              <Spinner className="size-4" />
              {uploadProgress != null && <span>{uploadProgress}%</span>}
            </span>
          ) : undefined}
        </ActionTile>
      )}
      {canGenerate && (
        <ActionTile
          ratio={ratio}
          icon={<Sparkles className="size-5" aria-hidden="true" />}
          label="Create with AI"
          onClick={() => onGenerate?.()}
        />
      )}
    </>
  );

  const renderGrid = () => {
    if (isError) {
      return (
        <p className="text-sm text-slate-500">
          We couldn't load backgrounds.{" "}
          <Button variant="link" size="inline" onClick={() => void refetch()}>
            Try again
          </Button>
        </p>
      );
    }

    const loading = isLoading && !waitingForCoach;
    const empty = !loading && assets.length === 0;

    return (
      <>
        {empty && filtering && (
          <p className="text-sm text-slate-500">
            No backgrounds match. Try fewer filters.{" "}
            <Button variant="link" size="inline" onClick={clear}>
              Clear
            </Button>
          </p>
        )}
        {empty && !filtering && !showSuggested && (
          <p className="text-sm text-slate-500">No backgrounds yet. Upload a photo or create one with AI.</p>
        )}
        <div
          className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(96px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(120px,1fr))]"
          data-testid="background-picker-grid"
        >
          {pendingRequests.map(request => (
            <HoloShimmer
              key={`request-${request.id}`}
              aspectRatio={ratio}
              label={creatingLabel(request.estimatedWaitSeconds)}
              className="rounded-lg"
            />
          ))}
          {loading
            ? Array.from({ length: SKELETON_TILES }, (_, i) => (
                <Skeleton key={`skeleton-${i}`} className={cn("w-full rounded-lg", aspectClass(ratio))} />
              ))
            : assets.map(asset => (
                <BackgroundTileCrop
                  key={asset.id}
                  asset={asset}
                  ratio={ratio}
                  selected={selectedAssetId === asset.id}
                  disabled={excluded.has(asset.id)}
                  onClick={() => onSelect(asset)}
                />
              ))}
          {!loading && (!empty || !filtering) && actionTiles}
        </div>
        <div ref={sentinelRef} aria-hidden="true" />
      </>
    );
  };

  const fileInput = canUpload && (
    <input
      ref={inputRef}
      type="file"
      accept={UPLOAD_ACCEPT}
      className="hidden"
      onChange={onFile}
      data-testid="background-picker-upload-input"
    />
  );

  if (!searchable) {
    return (
      <div className={cn("flex flex-col gap-3", className)} data-testid="background-picker">
        <div
          className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(96px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(120px,1fr))]"
          data-testid="background-picker-suggested"
        >
          {suggested.map(background => (
            <BackgroundTileCrop
              key={background.assetId}
              asset={suggestedTile(background)}
              ratio={ratio}
              selected={selectedAssetId === background.assetId}
              disabled={excluded.has(background.assetId)}
              onClick={() => onSelect(background)}
              badge={
                background.isDefault ? (
                  <Badge variant="secondary" size="small" className="absolute top-1 right-1">
                    Default
                  </Badge>
                ) : undefined
              }
            />
          ))}
          {actionTiles}
        </div>
        {fileInput}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3", className)} data-testid="background-picker">
      <div className="flex flex-wrap items-center gap-3">
        <InputGroup
          className="min-w-50 flex-1"
          control={controlProps => (
            <Input
              {...controlProps}
              value={searchText}
              placeholder="Search backgrounds…"
              aria-label="Search backgrounds"
              onChange={event => setSearchText(event.target.value)}
            />
          )}
          prepend={<Search />}
        />
        {mode === "coach" && (
          <Tabs value={scope} onValueChange={value => setScope(value as Scope)}>
            <Tabs.List variant="line">
              <Tabs.Trigger value="all">All</Tabs.Trigger>
              <Tabs.Trigger value="coach">Your library</Tabs.Trigger>
              <Tabs.Trigger value="global">SocialCoach</Tabs.Trigger>
            </Tabs.List>
          </Tabs>
        )}
        {mode === "admin" && (
          <>
            <ScopeToggle scope={scope === "coach" ? "coach" : "global"} onScopeChange={setScope} />
            {scope === "coach" && <CoachPicker coach={coach} onCoachChange={setCoach} />}
          </>
        )}
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="background-picker-chips">
        {chipKeys.map(key => (
          <FacetChip
            key={key}
            chip={key}
            values={chips[key] ?? []}
            onChange={values => setChips(current => ({ ...current, [key]: values }))}
          />
        ))}
        {hasInput && (
          <Button variant="link" size="inline" className="shrink-0" onClick={clear}>
            Clear
          </Button>
        )}
      </div>

      {showSuggested && (
        <div className="flex flex-col gap-2" data-testid="background-picker-suggested">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Suggested for this design</span>
          <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(96px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(120px,1fr))]">
            {suggested.map(background => (
              <BackgroundTileCrop
                key={background.assetId}
                asset={suggestedTile(background)}
                ratio={ratio}
                selected={selectedAssetId === background.assetId}
                disabled={excluded.has(background.assetId)}
                onClick={() => onSelect(background)}
                badge={
                  background.isDefault ? (
                    <Badge variant="secondary" size="small" className="absolute top-1 right-1">
                      Default
                    </Badge>
                  ) : undefined
                }
              />
            ))}
          </div>
          <Separator />
        </div>
      )}

      {renderGrid()}

      {fileInput}
    </div>
  );
};
